/**
 * Document Approval Event Types
 * 
 * Defines the event structure for document approval state changes
 * emitted from DocumentModal to FinanceApprovalQueue
 */

export type DocumentApprovalAction = 
  | 'submitted_for_approval'
  | 'approved'
  | 'rejected'
  | 'delegated'
  | 'status_changed';

export interface DocumentApprovalEvent {
  // Identity
  document_id: string;
  doc_type: string;
  doc_number: string;
  
  // Action
  action: DocumentApprovalAction;
  
  // Before/After
  status_before?: string;
  status_after?: string;
  
  // Context
  approval_notes?: string;
  rejection_reason?: string;
  delegated_to?: number;
  delegated_to_name?: string;
  
  // Financial
  total_amount?: number;
  currency?: string;
  
  // User
  approved_by?: string;
  rejected_by?: string;
  
  // Timestamp (added by system)
  timestamp?: string;
}

/**
 * Determine the approval action based on status transition
 */
export function determineApprovalAction(
  beforeStatus: string | undefined,
  afterStatus: string
): DocumentApprovalAction {
  if (afterStatus === 'pending_approval' && beforeStatus !== 'pending_approval') {
    return 'submitted_for_approval';
  }
  if (afterStatus === 'approved' && beforeStatus !== 'approved') {
    return 'approved';
  }
  if (afterStatus === 'rejected' && beforeStatus !== 'rejected') {
    return 'rejected';
  }
  return 'status_changed';
}

/**
 * Emit a document approval event
 */
export function emitDocumentApprovalEvent(event: DocumentApprovalEvent): void {
  const eventWithTimestamp: DocumentApprovalEvent = {
    ...event,
    timestamp: new Date().toISOString(),
  };
  
  window.dispatchEvent(
    new CustomEvent('finance:approval-event', { 
      detail: eventWithTimestamp 
    })
  );
}
